import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CALENDLY_URL } from "../data/content";

// Context-aware mobile action bar for service pages. Appears only after the
// hero's primary CTA scrolls out of view, so it never stacks with it. One
// action per service: booking call (Calendly) or the SuburbIQ order jump.
// Toggles a body class so the Ask 80 Metrics launcher moves clear of the bar.
const BAR_CTA = {
  "buyer-advocacy": { label: "Book a Buyer Advocacy call", href: CALENDLY_URL, external: true },
  "property-portfolio-review": { label: "Book an assessment call", href: CALENDLY_URL, external: true },
  "vendor-advocacy": { label: "Book a Vendor Advocacy call", href: CALENDLY_URL, external: true },
  suburbiq: { label: "Buy SuburbIQ — $220", href: "/suburbiq#order", external: false },
};

export const MobileActionBar = ({ serviceKey, watchTestId }) => {
  const [visible, setVisible] = useState(false);
  const barRef = useRef(null);
  const cta = BAR_CTA[serviceKey];
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const heroCta = document.querySelector(`[data-testid="${watchTestId}"]`);
    if (!heroCta || !("IntersectionObserver" in window)) return undefined;
    const obs = new IntersectionObserver(([entry]) => setVisible(!entry.isIntersecting), { threshold: 0 });
    obs.observe(heroCta);
    return () => obs.disconnect();
  }, [watchTestId]);

  useEffect(() => {
    document.body.classList.toggle("mobile-action-bar-visible", visible);
    return () => document.body.classList.remove("mobile-action-bar-visible");
  }, [visible]);

  const jump = (e) => {
    if (cta.external) return;
    const el = document.getElementById("order");
    if (!el) return; // fall back to the anchor href
    e.preventDefault();
    el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
    const first = el.querySelector("button, input, select, textarea, a");
    first?.focus({ preventScroll: true });
  };

  if (!cta) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={barRef}
          initial={reduced ? false : { y: 80 }}
          animate={{ y: 0 }}
          exit={reduced ? {} : { y: 80 }}
          transition={reduced ? { duration: 0 } : { duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="lg:hidden fixed bottom-0 left-0 right-0 z-[55] bg-charcoal border-t border-gold/40 px-4 pt-3"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
          data-testid="mobile-action-bar"
        >
          {cta.external ? (
            <a
              href={cta.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-action-bar-cta"
              className="block w-full px-6 py-3.5 bg-gold text-ink text-center text-sm font-semibold"
            >
              {cta.label}
            </a>
          ) : (
            <Link
              to={cta.href}
              onClick={jump}
              data-testid="mobile-action-bar-cta"
              className="block w-full px-6 py-3.5 bg-gold text-ink text-center text-sm font-semibold"
            >
              {cta.label}
            </Link>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
